import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import CTA from "@/components/CTA";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";
import MiniatureTransparencyMap from "@/components/MiniatureTransparencyMap";
import LottieAnimation from "@/components/LottieAnimation";
import { useEffect, useRef, useState } from "react";

export default function Index() {
  const section01Ref = useRef<HTMLDivElement>(null);
  const [shouldPlayAnimation, setShouldPlayAnimation] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !shouldPlayAnimation) {
          setShouldPlayAnimation(true);
        }
      },
      { threshold: 0.3 }
    );

    if (section01Ref.current) {
      observer.observe(section01Ref.current);
    }

    return () => observer.disconnect();
  }, [shouldPlayAnimation]);
  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <Hero />

      {/* CTA Section */}
      <CTA />

      {/* Main Content - All existing sections */}
      <div className="w-full bg-brand-martinique">
        {/* Section 01: What is the HOLE Truth Project? */}
        <section
          id="section-01"
          ref={section01Ref}
          className="w-full bg-brand-martinique border-black/10"
        >
          <div className="max-w-[1440px] mx-auto px-8 pt-4">
            <Breadcrumbs
              items={[{ label: "What is the HOLE Truth Project?" }]}
              variant="dark"
            />
          </div>
          <div className="max-w-[1440px] mx-auto px-8 py-8 lg:py-8 lg:pb-[120px]">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: Text Content */}
              <div>
                <div className="mb-8 lg:mb-[62px]">
                  <h2 className="font-agency text-h2 font-bold text-white">
                    <span className="text-brand-orange">01</span>
                    <br className="lg:hidden" />
                    <span className="lg:inline"> </span>What is the HOLE Truth
                    Project?
                  </h2>
                </div>
                <div className="space-y-4 font-martianmono text-text-lg-custom text-brand-gray">
              <p>
                The HOLE Truth Project represents the public-facing efforts of
                the HOLE Foundation, which stands for Honest Oversight & Legal
                Ethics, a 501(c)(3) organization dedicated to illuminating the
                gaps where democracy falters. We know what you might think
                first, and that's by design—government secrecy often harms
                everyday people.
              </p>
              <p>
                Founded by Dr. Joseph T. Herrmann, M.D., the HOLE Foundation was
                established to democratize government accountability through
                artificial intelligence. We build and freely distribute
                AI-powered tools that transform every citizen into an effective
                government watchdog, eliminating barriers that have historically
                restricted transparency work to credentialed professionals and
                well-funded organizations.
              </p>
              <p>
                Our core belief is simple:{" "}
                <span className="font-bold">
                  If you're a citizen, you're qualified
                </span>
                . No credentials required, no gatekeeping tolerated. Every tool
                is free and open source, with success measured by citizen
                empowerment.
              </p>
                </div>
              </div>

              {/* Right: Animation - Plays once when scrolled into view */}
              <div className="flex items-center justify-center">
                <div className="w-full max-w-[500px] h-[500px] bg-brand-dark/5 rounded-lg flex items-center justify-center overflow-hidden">
                  {shouldPlayAnimation ? (
                    <LottieAnimation
                      src="/animations/HOLE-Abstract-Balls.lottie"
                      loop={false}
                      autoplay={true}
                      className="w-full h-full"
                      ariaLabel="HOLE Foundation abstract animation"
                    />
                  ) : (
                    <div className="w-full h-full" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 02: Transparency Map */}
        <section
          id="section-02"
          className="w-full bg-brand-martinique border-t border-black/10"
        >
          <div className="max-w-[1440px] mx-auto px-8 pt-4">
            <Breadcrumbs
              items={[{ label: "Transparency Map" }]}
              variant="dark"
            />
          </div>
          <div className="max-w-[1440px] mx-auto px-8 py-8 lg:py-8">
            <div className="mb-8 lg:mb-[96px]">
              <h2 className="font-agency text-h2 font-bold text-white">
                <span className="text-brand-orange">02</span>
                <br className="lg:hidden" />
                <span className="lg:inline"> </span>Transparency Map
              </h2>
            </div>
            <div className="space-y-4 mb-8 lg:mb-[32px]">
              <p className="font-martianmono text-text-lg-custom text-brand-gray">
                The Transparency Map is an interactive visualization of all 50
                states and federal transparency laws. With 51 different laws
                governing public records across the United States, navigating
                this maze can be daunting. Our map provides:
              </p>
              <ul className="space-y-2 pl-6 font-martianmono text-text-lg-custom text-brand-gray list-disc">
                <li>One-click access to your jurisdiction's rules</li>
                <li>
                  Interactive visualization of all 50 states + federal laws
                </li>
                <li>AI assistant trained on state-specific statutes</li>
                <li>Real-time updates as laws change</li>
              </ul>
              <p className="font-martianmono text-text-lg-custom text-brand-gray">
                Just click on your state to see what information you're entitled
                to and how to get it.
              </p>
            </div>
            <div className="flex justify-center mt-8 lg:mt-[32px] mb-8">
              <MiniatureTransparencyMap />
            </div>
          </div>
        </section>

        {/* Section 03: Transparency Wiki */}
        <section
          id="section-03"
          className="w-full bg-brand-martinique border-t border-black/10"
        >
          <div className="max-w-[1440px] mx-auto px-8 py-8 lg:py-8 lg:pb-[90px]">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-8">
              <div>
                <h2 className="font-agency text-h2 font-bold text-white mb-8 lg:mb-0">
                  <span className="text-brand-orange">03</span>
                  <br className="lg:hidden" />
                  <span className="lg:inline"> </span>Transparency
                  <br />
                  Wiki
                </h2>
              </div>
              <div className="space-y-6">
                <div className="space-y-4">
                  <p className="font-martianmono text-text-md-custom text-brand-gray">
                    There are fifty-one separate laws governing public records
                    in the United States, one for each state and Federal
                    Records. The Transparency Wiki provides detailed information
                    about:
                  </p>
                  <ul className="space-y-2 pl-6 font-martianmono text-text-md-custom text-brand-gray list-disc">
                    <li>Who can request records in your jurisdiction</li>
                    <li>What types of records are available</li>
                    <li>How long agencies have to respond</li>
                    <li>What appeals process is available</li>
                    <li>Common exemptions and how to challenge them</li>
                  </ul>
                  <p className="font-martianmono text-text-md-custom text-brand-gray">
                    Our wiki is constantly updated with new legal developments,
                    case law, and practical tips from successful requesters.
                  </p>
                </div>
                <div className="bg-brand-dark rounded-lg p-6 flex items-center justify-center min-h-[250px]">
                  <a
                    href="/wiki"
                    className="inline-block px-6 py-3 bg-brand-orange text-white font-agency text-h6 font-bold rounded hover:bg-brand-orange/90 transition-colors"
                  >
                    Explore the Wiki
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 04: FOIA Request Generator */}
        <section
          id="section-04"
          className="w-full bg-brand-martinique border-t border-black/10"
        >
          <div className="max-w-[1440px] mx-auto px-8 py-8 lg:py-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-8">
              <div>
                <h2 className="font-agency text-h2 font-bold text-white">
                  <span className="text-brand-orange">04</span>
                  <br className="lg:hidden" />
                  <span className="lg:inline"> </span>FOIA Request
                  <br className="hidden lg:block" /> Generator
                </h2>
              </div>
              <div className="space-y-6">
                <div className="space-y-4">
                  <p className="font-martianmono text-text-lg-custom text-brand-gray">
                    The FOIA Request Generator is an AI-powered tool designed to
                    streamline your access to public records. By connecting to
                    the HOLE Foundation's comprehensive database of FOIA
                    statutes and our detailed analyses, this advanced document
                    generator:
                  </p>
                  <ul className="space-y-2 pl-6 font-martianmono text-text-lg-custom text-brand-gray list-disc">
                    <li>Is trained on thousands of successful requests</li>
                    <li>
                      Predicts likely exemptions and provides counter-arguments
                    </li>
                    <li>
                      Creates legally optimized requests for your specific
                      jurisdiction
                    </li>
                    <li>
                      Includes agency-specific customization for maximum success
                    </li>
                  </ul>
                  <p className="font-martianmono text-text-lg-custom text-brand-gray">
                    With this tool, you enhance your chances of obtaining the
                    records you desire, even without legal expertise.
                  </p>
                </div>
                <div className="bg-brand-dark rounded-lg p-6 flex items-center justify-center min-h-[250px]">
                  <a
                    href="/foia-generator"
                    className="inline-block px-6 py-3 bg-brand-orange text-white font-agency text-h6 font-bold rounded hover:bg-brand-orange/90 transition-colors"
                  >
                    Launch FOIA Generator
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 05: Our Mission & Vision */}
        <section
          id="section-05"
          className="w-full bg-gradient-to-b from-brand-martinique to-brand-martinique relative"
        >
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-[1440px] mx-auto px-8 py-8 lg:py-16">
            <div className="mb-8 lg:mb-12">
              <h2 className="font-agency text-h2 font-bold text-white">
                <span className="text-brand-orange">05</span>
                <br className="lg:hidden" />
                <span className="lg:inline"> </span>Our Mission & Vision
              </h2>
            </div>

            {/* Mission and Vision Grid */}
            <div className="grid lg:grid-cols-2 gap-8 mb-8 lg:mb-12">
              <div className="bg-brand-dark rounded-lg p-6 space-y-4 shadow-md">
                <h3 className="font-agency text-h3 font-bold text-white">
                  Our Mission
                </h3>
                <p className="font-martianmono text-text-md-custom text-brand-gray">
                  The HOLE Foundation deploys artificial intelligence to
                  democratize government accountability. We build and freely
                  distribute AI-powered transparency tools that transform every
                  citizen into an effective government watchdog.
                </p>
                <h4 className="font-agency text-h4 font-bold text-white">
                  Core Mission Elements:
                </h4>
                <ul className="space-y-2 pl-6 font-martianmono text-text-md-custom text-brand-gray list-disc">
                  <li>
                    Democratize Access: Make advanced transparency tools
                    available to every citizen
                  </li>
                  <li>
                    Deploy AI: Use cutting-edge artificial intelligence to level
                    the playing field
                  </li>
                  <li>
                    Build Infrastructure: Create systematic accountability
                    systems, not one-off tools
                  </li>
                  <li>
                    Educate Citizens: Teach AI literacy for democratic
                    participation
                  </li>
                </ul>
              </div>

              <div className="bg-brand-dark rounded-lg p-6 space-y-4 shadow-md">
                <h3 className="font-agency text-h3 font-bold text-white">
                  Our Vision
                </h3>
                <p className="font-martianmono text-text-md-custom text-brand-gray">
                  We envision a democracy where artificial intelligence
                  eliminates the power imbalance between citizens and
                  institutions - where anyone with an internet connection can
                  expose corruption, track government spending, and enforce
                  accountability as effectively as major news organizations.
                </p>
                <h4 className="font-agency text-h4 font-bold text-white">
                  The Future We're Building:
                </h4>
                <ul className="space-y-2 pl-6 font-martianmono text-text-md-custom text-brand-gray list-disc">
                  <li>
                    Every citizen equipped with AI tools that match
                    institutional capabilities
                  </li>
                  <li>
                    Government patterns detected in real-time through systematic
                    monitoring
                  </li>
                  <li>
                    Corruption prevented through predictive analytics, not
                    exposed after damage
                  </li>
                  <li>
                    Democratic participation measured in millions of citizen
                    investigators
                  </li>
                </ul>
              </div>
            </div>

            {/* Our Values */}
            <div className="space-y-6">
              <h3 className="font-agency text-h3 font-bold text-white">
                Our Values
              </h3>
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="bg-brand-dark/20 rounded-lg p-6 shadow-lg space-y-2">
                  <h4 className="font-agency text-h4 font-bold text-white">
                    Radical Access
                  </h4>
                  <p className="text-brand-orange font-agency text-text-md-custom font-bold">
                    "If you're a citizen, you're qualified."
                  </p>
                  <ul className="space-y-1 pl-6 font-martianmono text-text-sm-custom text-brand-gray list-disc">
                    <li>No credentials required, no gatekeeping tolerated</li>
                    <li>Every tool free and open source</li>
                    <li>Knowledge shared immediately and completely</li>
                    <li>Success measured by citizen empowerment</li>
                  </ul>
                </div>

                <div className="bg-brand-dark/20 rounded-lg p-6 shadow-lg space-y-2">
                  <h4 className="font-agency text-h4 font-bold text-white">
                    Technological Equity
                  </h4>
                  <p className="text-brand-orange font-agency text-text-md-custom font-bold">
                    "AI for the people, not just the powerful."
                  </p>
                  <ul className="space-y-1 pl-6 font-martianmono text-text-sm-custom text-brand-gray list-disc">
                    <li>
                      The same AI that powers Fortune 500 companies should
                      empower individual citizens
                    </li>
                    <li>Advanced technology deployed for public good</li>
                    <li>Continuous innovation to maintain citizen advantage</li>
                    <li>Open source commitment ensures no vendor lock-in</li>
                  </ul>
                </div>

                <div className="bg-brand-dark/20 rounded-lg p-6 shadow-lg space-y-2">
                  <h4 className="font-agency text-h4 font-bold text-white">
                    Systematic Accountability
                  </h4>
                  <p className="text-brand-orange font-agency text-text-md-custom font-bold">
                    "We don't wait for scandals - we prevent them."
                  </p>
                  <ul className="space-y-1 pl-6 font-martianmono text-text-sm-custom text-brand-gray list-disc">
                    <li>Continuous monitoring, not reactive investigations</li>
                    <li>Pattern detection at scale through AI</li>
                    <li>Predictive analytics to identify risk before harm</li>
                    <li>Infrastructure building, not just tool creation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 06: Contact & Legal Information */}
        <section
          id="section-06"
          className="w-full bg-brand-martinique border-t border-black/10"
        >
          <div className="max-w-[1440px] mx-auto px-8 py-8 lg:py-16">
            <div className="mb-8 lg:mb-12">
              <h2 className="font-agency text-h2 font-bold text-white">
                <span className="text-brand-orange">06</span>
                <br className="lg:hidden" />
                <span className="lg:inline"> </span>Contact & Legal Information
              </h2>
            </div>

            {/* Contact and Legal Grid */}
            <div className="grid lg:grid-cols-2 gap-8 mb-8">
              <div className="bg-brand-dark rounded-lg p-6 space-y-4 shadow-md">
                <h3 className="font-agency text-h3 font-bold text-white">
                  Contact Information
                </h3>
                <div className="space-y-4 font-martianmono text-text-md-custom text-brand-gray">
                  <div>
                    <p>The HOLE Foundation</p>
                    <p>Honest Oversight & Legal Ethics</p>
                    <p className="italic">
                      "Shining light in the holes where democracy goes dark"
                    </p>
                  </div>
                  <div>
                    <p>Headquarters:</p>
                    <p>300 Delaware Ave, Ste 210 PMB# 765</p>
                    <p>Wilmington, DE 19801</p>
                  </div>
                  <div>
                    <p>Contact:</p>
                    <ul className="pl-6 space-y-1">
                      <li>
                        General:{" "}
                        <a
                          href="mailto:info@theholetruth.org"
                          className="text-brand-orange hover:underline"
                        >
                          info@theholetruth.org
                        </a>
                      </li>
                      <li>
                        Founder:{" "}
                        <a
                          href="mailto:joe@theholetruth.org"
                          className="text-brand-orange hover:underline"
                        >
                          joe@theholetruth.org
                        </a>
                      </li>
                      <li>
                        Tips:{" "}
                        <a
                          href="mailto:tips@theholetruth.org"
                          className="text-brand-orange hover:underline"
                        >
                          tips@theholetruth.org
                        </a>
                      </li>
                      <li>Phone: (302) 314-3354</li>
                      <li>Fax: (302) 202-2571</li>
                    </ul>
                  </div>
                  <div>
                    <p>Digital Presence:</p>
                    <ul className="pl-6 space-y-1">
                      <li>
                        Foundation:{" "}
                        <a
                          href="https://www.theholefoundation.org"
                          className="text-brand-orange hover:underline"
                        >
                          www.theholefoundation.org
                        </a>
                      </li>
                      <li>
                        Platform:{" "}
                        <a
                          href="https://www.theholetruth.org"
                          className="text-brand-orange hover:underline"
                        >
                          www.theholetruth.org
                        </a>
                      </li>
                      <li>
                        GitHub:{" "}
                        <a
                          href="https://github.com/theholefoundation"
                          className="text-brand-orange hover:underline"
                        >
                          github.com/theholefoundation
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-brand-dark rounded-lg p-6 space-y-4 shadow-md">
                <h3 className="font-agency text-h3 font-bold text-white">
                  Legal Status & Compliance
                </h3>
                <div className="space-y-4 font-martianmono text-text-md-custom text-brand-gray">
                  <div>
                    <p>Corporate Structure:</p>
                    <ul className="pl-6 space-y-1">
                      <li>
                        Delaware Non-Stock Corporation (Filed: July 2, 2025)
                      </li>
                      <li>
                        IRS 501(c)(3) Private Foundation (Approved: August 12,
                        2025)
                      </li>
                      <li>EIN: 39-3010423</li>
                      <li>Annual Form 990-PF Required</li>
                    </ul>
                  </div>
                  <div>
                    <p>Governance:</p>
                    <ul className="pl-6 space-y-1">
                      <li>
                        Founder/Executive Director: Joseph T. Herrmann, M.D.
                      </li>
                      <li>
                        Director of Public Records & Compliance: Candace Storey
                      </li>
                      <li>Board of Directors: [In formation]</li>
                    </ul>
                  </div>
                  <div>
                    <p>Compliance Commitments:</p>
                    <ul className="pl-6 space-y-1">
                      <li>Full financial transparency</li>
                      <li>Annual impact reporting</li>
                      <li>Open board meetings</li>
                      <li>Published conflict of interest policy</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Our Promise */}
            <div className="bg-brand-dark rounded-lg p-6 max-w-[672px] mx-auto shadow-md">
              <h3 className="font-agency text-h3 font-bold text-white text-center mb-6">
                Our Promise to Citizens
              </h3>
              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  <p className="font-martianmono text-text-md-custom font-bold text-white mb-2">
                    We Will Always:
                  </p>
                  <ul className="space-y-1 pl-6 font-martianmono text-text-md-custom text-brand-gray list-disc">
                    <li>Provide every tool completely free</li>
                    <li>Share all methods and code openly</li>
                    <li>Support any citizen seeking accountability</li>
                    <li>Fight institutional gatekeeping</li>
                    <li>Teach you to use AI as a tool for democracy</li>
                  </ul>
                </div>
                <div>
                  <p className="font-martianmono text-text-md-custom font-bold text-white mb-2">
                    We Will Never:
                  </p>
                  <ul className="space-y-1 pl-6 font-martianmono text-text-md-custom text-brand-gray list-disc">
                    <li>Require credentials or qualifications</li>
                    <li>Create paid tiers or premium features</li>
                    <li>Restrict access to our tools</li>
                    <li>Keep our strategies secret</li>
                    <li>Compromise on transparency</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
}
